import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type {
  FulfillmentEngineFulfillmentCenter,
  FulfillmentEngineFulfillmentCenterCreate,
  FulfillmentEngineFulfillmentCenterUpdate,
  FulfillmentEngineGetAllFulfillmentCentersV1Params,
  FulfillmentEngineGetFulfillmentCenterV1Params,
  FulfillmentEngineGetPlanV1Params,
  FulfillmentEngineFEOrder,
  FulfillmentEnginePlan,
  FulfillmentEngineGetAllGlobalConfigsV1Params,
  FulfillmentEngineGlobalConfig,
  FulfillmentEnginePlanSummary,
  FulfillmentEngineOrderSummary,
  FulfillmentEnginePlanDetails,
} from '@shipium-corp/shipium-ts-types';
import type { Injections } from '../services/injections';
import type { StoreModel } from '..';
import type { FulfillmentEngineGetOrderParams, ShipmentSummary } from '../services/FulfillmentEngineService/types';
import {
  formatOrderSearchRequest,
  formatPartnerOrderSearchRequest,
  formatPlanSearchRequest,
  formatShipmentSearchRequest,
} from '../../pages/FulfillmentEngine/utils';
import type { FulfillmentEngineSearchValues } from '../../pages/FulfillmentEngine/FulfillmentEngineSearch';
import { parseISO } from 'date-fns';

export interface FulfillmentEngineModel {
  // data
  error: string | null;
  fulfillmentCenterConfigs: FulfillmentEngineFulfillmentCenter[];
  globalConfigs: FulfillmentEngineGlobalConfig[];
  plan: FulfillmentEnginePlan | null;
  planDetails: FulfillmentEnginePlanDetails | null;
  planSummaries: FulfillmentEnginePlanSummary[];
  orderSummaries: FulfillmentEngineOrderSummary[];
  shipmentSummaries: ShipmentSummary[];
  order: FulfillmentEngineFEOrder | null;
  orders: FulfillmentEngineFEOrder[];
  // actions
  setPlan: Action<FulfillmentEngineModel, FulfillmentEnginePlan | null>;
  setPlanDetails: Action<FulfillmentEngineModel, FulfillmentEnginePlanDetails | null>;
  setPlanSummaries: Action<FulfillmentEngineModel, FulfillmentEnginePlanSummary[]>;
  setOrderSummaries: Action<FulfillmentEngineModel, FulfillmentEngineOrderSummary[]>;
  setShipmentSummaries: Action<FulfillmentEngineModel, ShipmentSummary[]>;
  setOrders: Action<FulfillmentEngineModel, FulfillmentEngineFEOrder[]>;
  setOrder: Action<FulfillmentEngineModel, FulfillmentEngineFEOrder | null>;
  setError: Action<FulfillmentEngineModel, string | null>;
  setFulfillmentCenterConfigs: Action<FulfillmentEngineModel, FulfillmentEngineFulfillmentCenter[]>;
  setGlobalConfigs: Action<FulfillmentEngineModel, FulfillmentEngineGlobalConfig[]>;
  fetchAllFulfillmentCenterConfigs: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGetAllFulfillmentCentersV1Params,
    Injections,
    StoreModel,
    Promise<void>
  >;
  fetchFulfillmentCenterConfigs: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGetFulfillmentCenterV1Params,
    Injections,
    StoreModel,
    Promise<void>
  >;
  createFulfillmentCenterConfig: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineFulfillmentCenterCreate,
    Injections,
    StoreModel,
    Promise<void>
  >;
  updateFulfillmentCenterConfig: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineFulfillmentCenterUpdate & Pick<FulfillmentEngineFulfillmentCenter, 'fulfillmentCenterId'>,
    Injections,
    StoreModel,
    Promise<void>
  >;
  fetchGlobalConfigs: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGetAllGlobalConfigsV1Params,
    Injections,
    StoreModel,
    Promise<void>
  >;
  createGlobalConfig: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGlobalConfig,
    Injections,
    StoreModel,
    Promise<void>
  >;
  updateGlobalConfig: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGlobalConfig,
    Injections,
    StoreModel,
    Promise<void>
  >;
  searchPlanSummaries: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineSearchValues,
    Injections,
    StoreModel,
    Promise<FulfillmentEnginePlanSummary[] | undefined>
  >;
  searchOrderSummaries: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineSearchValues & { searchByPartnerOrderId: boolean },
    Injections,
    StoreModel,
    Promise<FulfillmentEngineOrderSummary[] | undefined>
  >;
  searchShipmentSummaries: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineSearchValues,
    Injections,
    StoreModel,
    Promise<ShipmentSummary[] | undefined>
  >;
  fetchPlan: Thunk<FulfillmentEngineModel, FulfillmentEngineGetPlanV1Params, Injections, StoreModel, Promise<void>>;
  fetchPlanDetails: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGetPlanV1Params,
    Injections,
    StoreModel,
    Promise<void>
  >;
  fetchOrders: Thunk<
    FulfillmentEngineModel,
    FulfillmentEngineGetPlanV1Params,
    Injections,
    StoreModel,
    Promise<FulfillmentEngineFEOrder[] | undefined>
  >;
  fetchOrder: Thunk<FulfillmentEngineModel, FulfillmentEngineGetOrderParams, Injections, StoreModel, Promise<void>>;
}

export const fulfillmentEngineModel: FulfillmentEngineModel = {
  fulfillmentCenterConfigs: [],
  globalConfigs: [],
  error: null,
  planSummaries: [],
  orderSummaries: [],
  shipmentSummaries: [],
  plan: null,
  planDetails: null,
  orders: [],
  order: null,
  setPlan: action((state, payload) => {
    state.plan = payload;
  }),
  setPlanDetails: action((state, payload) => {
    state.planDetails = payload;
  }),
  setPlanSummaries: action((state, payload) => {
    state.planSummaries = payload;
  }),
  setOrderSummaries: action((state, payload) => {
    state.orderSummaries = payload;
  }),
  setShipmentSummaries: action((state, payload) => {
    state.shipmentSummaries = payload;
  }),
  setOrders: action((state, payload) => {
    state.orders = payload;
  }),
  setOrder: action((state, payload) => {
    state.order = payload;
  }),
  setError: action((state, payload) => {
    state.error = payload;
  }),
  setGlobalConfigs: action((state, payload) => {
    state.globalConfigs = payload;
  }),
  setFulfillmentCenterConfigs: action((state, payload) => {
    state.fulfillmentCenterConfigs = payload;
  }),
  fetchAllFulfillmentCenterConfigs: thunk(async (actions, payload, { injections }) => {
    try {
      const fulfillmentCenterConfigs =
        await injections.fulfillmentEngineService.fetchAllFulfillmentCenterConfigs(payload);
      actions.setFulfillmentCenterConfigs(fulfillmentCenterConfigs);
    } catch (e) {
      actions.setFulfillmentCenterConfigs([]);
      actions.setError(e.message);
    }
  }),
  fetchFulfillmentCenterConfigs: thunk(async (actions, payload, { injections }) => {
    try {
      const fulfillmentCenterConfigs = await injections.fulfillmentEngineService.fetchFulfillmentCenterConfigs(payload);
      actions.setFulfillmentCenterConfigs(fulfillmentCenterConfigs);
    } catch (e) {
      actions.setFulfillmentCenterConfigs([]);
      actions.setError(e.message);
    }
  }),
  createFulfillmentCenterConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const fulfillmentCenterConfig = await injections.fulfillmentEngineService.createFulfillmentCenterConfig(payload);
      actions.setFulfillmentCenterConfigs([
        ...getStoreState().fulfillmentEngine.fulfillmentCenterConfigs,
        fulfillmentCenterConfig,
      ]);
    } catch (e) {
      actions.setError(e.message);
    }
  }),
  updateFulfillmentCenterConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const fulfillmentCenterConfig = await injections.fulfillmentEngineService.updateFulfillmentCenterConfig(
        payload.fulfillmentCenterId!,
        payload,
      );
      actions.setFulfillmentCenterConfigs(
        getStoreState().fulfillmentEngine.fulfillmentCenterConfigs.map((config) =>
          config.fulfillmentCenterId === fulfillmentCenterConfig.fulfillmentCenterId ? fulfillmentCenterConfig : config,
        ),
      );
    } catch (e) {
      actions.setError(e.message);
    }
  }),
  fetchGlobalConfigs: thunk(async (actions, payload, { injections }) => {
    try {
      const globalConfigs = await injections.fulfillmentEngineService.fetchGlobalConfigs(payload);
      actions.setGlobalConfigs(globalConfigs);
    } catch (e) {
      actions.setGlobalConfigs([]);
      actions.setError(e.message);
    }
  }),
  createGlobalConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const globalConfig = await injections.fulfillmentEngineService.createGlobalConfig(payload);
      actions.setGlobalConfigs([...getStoreState().fulfillmentEngine.globalConfigs, globalConfig]);
    } catch (e) {
      actions.setError(e.message);
    }
  }),
  updateGlobalConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const globalConfig = await injections.fulfillmentEngineService.updateGlobalConfig(
        payload.globalConfigId!,
        payload,
      );
      actions.setGlobalConfigs(
        getStoreState().fulfillmentEngine.globalConfigs.map((config) =>
          config.globalConfigId === globalConfig.globalConfigId ? globalConfig : config,
        ),
      );
    } catch (e) {
      actions.setError(e.message);
    }
  }),
  searchPlanSummaries: thunk(async (actions, payload, { injections }) => {
    try {
      const formattedPayload = formatPlanSearchRequest(payload);
      const plans = await injections.fulfillmentEngineService.searchPlansAndOrders(formattedPayload);

      if (plans?.length) {
        const dateSortedPlans = plans.sort(
          (a, b) => parseISO(b.creationTimeStamp).getTime() - parseISO(a.creationTimeStamp).getTime(),
        );

        actions.setPlanSummaries(dateSortedPlans);
      } else {
        actions.setPlanSummaries([]);
      }

      return plans;
    } catch (error) {
      actions.setPlanSummaries([]);
      actions.setError(error.message);
    }
  }),
  searchOrderSummaries: thunk(async (actions, payload, { injections }) => {
    try {
      const formattedPayload = payload.searchByPartnerOrderId
        ? formatPartnerOrderSearchRequest(payload)
        : formatOrderSearchRequest(payload);

      const plans = await injections.fulfillmentEngineService.searchPlansAndOrders(formattedPayload);

      if (plans?.some((plan) => plan.orders.length > 0)) {
        const dateSortedPlans = plans.sort(
          (a, b) => parseISO(b.creationTimeStamp).getTime() - parseISO(a.creationTimeStamp).getTime(),
        );
        const orderData = dateSortedPlans.flatMap((plan) => plan.orders);

        actions.setOrderSummaries(orderData);
        return orderData;
      } else {
        actions.setOrderSummaries([]);
      }

      return [];
    } catch (error) {
      actions.setOrderSummaries([]);
      actions.setError(error.message);
    }
  }),
  searchShipmentSummaries: thunk(async (actions, payload, { injections }) => {
    //we need to get the shipment data from plans
    //API cant search directly for shipment details
    //or get them from the plan summary
    try {
      const formattedPayload = formatShipmentSearchRequest(payload);
      const plans = await injections.fulfillmentEngineService.searchPlansAndOrders(formattedPayload);

      // if plans contain shipments, fetch and filter for them
      const proposedShipmentIds = payload.searchValue.split(/\s*,\s*/);
      if (
        proposedShipmentIds.length > 0 &&
        plans?.some((plan) => plan.orders.some((order) => order.proposedShipmentCount > 0))
      ) {
        let newProposedShipments: ShipmentSummary[] = [];

        // fetch orders within each plan and filter for matching shipments
        for (const plan of plans) {
          const orders = await injections.fulfillmentEngineService.fetchOrders({
            planId: plan.planId,
          });

          // process each order to gather the shipment summaries
          if (orders && orders.length > 0) {
            for (const order of orders) {
              // filter proposed shipments by the provided IDs
              const matchingShipments = order.proposedShipments.filter((shipment) =>
                proposedShipmentIds.includes(shipment.proposedShipmentId),
              );

              // map the matching shipments and add to the list
              newProposedShipments.push(
                ...matchingShipments.map((shipment) => ({
                  shipmentId: shipment.proposedShipmentId,
                  orderId: order.orderId,
                  partnerOrderId: order.partnerOrderId,
                  planId: order.planId,
                  creationTimeStamp: order.orderedDateTime,
                })),
              );
            }
          }
        }

        newProposedShipments = newProposedShipments.sort(
          (a, b) => parseISO(b.creationTimeStamp).getTime() - parseISO(a.creationTimeStamp).getTime(),
        );

        actions.setShipmentSummaries(newProposedShipments);
        return newProposedShipments;
      } else {
        actions.setShipmentSummaries([]);
      }

      return [];
    } catch (error) {
      actions.setShipmentSummaries([]);
      actions.setError(error.message);
    }
  }),
  fetchPlan: thunk(async (actions, payload, { injections }) => {
    try {
      const plan = await injections.fulfillmentEngineService.fetchPlan(payload);
      actions.setPlan(plan);
    } catch (e) {
      actions.setPlan(null);
      actions.setError(e.message);
    }
  }),
  fetchPlanDetails: thunk(async (actions, payload, { injections }) => {
    try {
      const planDetails = await injections.fulfillmentEngineService.fetchPlanDetails(payload);
      actions.setPlanDetails(planDetails);
    } catch (e) {
      actions.setPlanDetails(null);
      actions.setError(e.message);
    }
  }),
  fetchOrders: thunk(async (actions, payload, { injections }) => {
    try {
      const orders = await injections.fulfillmentEngineService.fetchOrders(payload);
      actions.setOrders(orders);

      return orders;
    } catch (e) {
      actions.setOrders([]);
      actions.setError(e.message);
    }
  }),
  fetchOrder: thunk(async (actions, payload, { injections }) => {
    try {
      const order = await injections.fulfillmentEngineService.fetchOrder(payload);
      actions.setOrder(order);
    } catch (e) {
      actions.setOrder(null);
      actions.setError(e.message);
    }
  }),
};
